// One glance at the state of an email transport: is it configured, waiting on a
// code, live — and is a live one actually delivering right now?
//
// Verification status and health are two different axes (plan §5 vs §6): a
// VERIFIED account whose password was revoked yesterday is still VERIFIED. The
// pill shows the verification state and overlays "Failing" when health says so,
// because that is the state the tenant must act on.

import { cn } from '@/constants/design-tokens';
import type { EmailTransportConfigMasked } from '@bbpos/types';

type PillTone = 'neutral' | 'warning' | 'success' | 'danger';

const TONE_CLASSES: Record<PillTone, string> = {
  neutral:
    'bg-neutral-100 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700',
  warning:
    'bg-warning-50 text-warning-700 ring-warning-200 dark:bg-warning-900/30 dark:text-warning-300 dark:ring-warning-800',
  success:
    'bg-success-50 text-success-700 ring-success-200 dark:bg-success-900/30 dark:text-success-300 dark:ring-success-800',
  danger:
    'bg-danger-50 text-danger-700 ring-danger-200 dark:bg-danger-900/30 dark:text-danger-300 dark:ring-danger-800',
};

const DOT_CLASSES: Record<PillTone, string> = {
  neutral: 'bg-neutral-400',
  warning: 'bg-warning-500',
  success: 'bg-success-500',
  danger: 'bg-danger-500',
};

export function emailStatusLabel(config: EmailTransportConfigMasked | null): {
  label: string;
  tone: PillTone;
} {
  if (!config) return { label: 'Not configured', tone: 'neutral' };
  if (config.verification_status === 'DISABLED') return { label: 'Disabled', tone: 'neutral' };
  if (config.verification_status === 'PENDING') {
    return { label: 'Pending verification', tone: 'warning' };
  }
  if (config.health_status === 'FAILING') return { label: 'Failing', tone: 'danger' };
  return { label: 'Verified', tone: 'success' };
}

export function EmailStatusPill({
  config,
  className,
}: {
  config: EmailTransportConfigMasked | null;
  className?: string;
}) {
  const { label, tone } = emailStatusLabel(config);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-all duration-200',
        TONE_CLASSES[tone],
        className,
      )}
      data-testid="email-status-pill"
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          DOT_CLASSES[tone],
          tone === 'danger' && 'animate-pulse',
        )}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}
